<div class="legal-page">
  <div class="legal-header">
    <h2>Terms of Service</h2>
  </div>

  <div class="legal-content">
    <p><strong>Last Updated:</strong> March 27, 2026</p>

    <h4>1. Acceptance of Terms</h4>
    <p>
      These Terms of Service ("Terms") govern your access to and use of the Livepass website, mobile application, and
      all associated services (collectively, the "Platform"), operated by MultiTV Solution ("Livepass", "Company",
      "We", "Us", "Our").
    </p>
    <p>
      By accessing, browsing, or using the Platform, you agree to be bound by these Terms and enter into a legally
      binding agreement with the Company. If you do not agree to these Terms, you must not use the Platform or any of
      its Services.
    </p>
    <p>
      These Terms apply to all users of the Platform, including without limitation users who are browsers, customers,
      merchants, or contributors of content.
    </p>

    <h4>2. Definitions</h4>
    <p>For the purposes of these Terms:</p>
    <ul>
      <li>"User" / "Customer" / "You" / "Your" shall mean any individual accessing or using the Platform.</li>
      <li>"Organizer" / "Merchant" shall mean any third party listing events, experiences, or services on the Platform.</li>
      <li>"Services" shall include event discovery, ticketing, bookings, payments, and related offerings provided through the Platform.</li>
      <li>"Content" shall include text, images, graphics, videos, listings, and all forms of information available on the Platform.</li>
    </ul>

    <h4>3. Nature of Services</h4>
    <p>
      The Platform is a technology intermediary that facilitates interaction and transactions between Users and
      Organizers.
    </p>
    <p>Livepass does not:</p>
    <ul>
      <li>own, operate, or control events</li>
      <li>guarantee the quality, safety, or legality of any event</li>
      <li>act as an agent of the Organizer</li>
    </ul>
    <p>All events, services, and experiences are the sole responsibility of the respective Organizer.</p>

    <h4>4. Eligibility</h4>
    <p>You represent and warrant that:</p>
    <ul>
      <li>you are at least eighteen (18) years of age, or</li>
      <li>you are accessing the Platform under the supervision of a parent or legal guardian</li>
    </ul>
    <p>and are competent to contract under the Indian Contract Act, 1872.</p>
    <p>
      If you use the Platform on behalf of any entity, you represent that you are duly authorized to bind such entity
      to these Terms.
    </p>

    <h4>5. User Account and Registration</h4>
    <p>To access certain features, you may be required to create an account.</p>
    <p>You agree to:</p>
    <ul>
      <li>provide true, accurate, and complete information</li>
      <li>maintain confidentiality of your login credentials</li>
      <li>be responsible for all activities under your account</li>
    </ul>
    <p>The Company shall not be liable for any loss arising from unauthorized use of your account.</p>
    <p>
      By registering, you consent to receive transactional and promotional communications from Livepass. You may opt
      out of promotional communications at any time.
    </p>

    <h4>6. Ticketing and Bookings</h4>
    <p>All tickets and bookings made through the Platform are:</p>
    <ul>
      <li>issued on behalf of the Organizer</li>
      <li>subject to Organizer-specific terms and conditions</li>
      <li>intended for personal use only</li>
    </ul>
    <p>Unless expressly permitted, tickets:</p>
    <ul>
      <li>are non-transferable</li>
      <li>may not be resold, exchanged, or used for commercial purposes</li>
    </ul>
    <p>Entry to any event is subject to:</p>
    <ul>
      <li>validation of ticket</li>
      <li>compliance with venue rules</li>
      <li>Organizer policies</li>
    </ul>

    <h4>7. Pricing and Payments</h4>
    <p>All prices are determined by the Organizer and displayed on the Platform.</p>
    <p>
      The Company may charge a convenience fee or platform fee, which shall be clearly indicated prior to completion
      of the transaction.
    </p>
    <p>
      Payments are processed through third-party payment gateways. Livepass does not store sensitive financial
      information.
    </p>
    <p>The Company shall not be responsible for:</p>
    <ul>
      <li>pricing errors beyond reasonable control</li>
      <li>failed transactions due to payment provider issues</li>
    </ul>

    <h4>8. Cancellations, Refunds and Rescheduling</h4>
    <p>All cancellations and refunds are subject to the policies of the respective Organizer.</p>
    <p>Unless otherwise specified:</p>
    <ul>
      <li>convenience/platform fees are non-refundable</li>
      <li>refunds, where applicable, shall be processed upon Organizer approval</li>
    </ul>
    <p>In case of:</p>
    <ul>
      <li><strong>Event Cancellation:</strong> refunds may be processed as instructed by the Organizer</li>
      <li><strong>Event Rescheduling:</strong> tickets may remain valid or refunds may be offered at Organizer discretion</li>
    </ul>
    <p>
      Refund timelines may vary but typically take 7–10 working days, subject to banking channels.
    </p>

    <h4>9. Event Changes and Disruptions</h4>
    <p>
      Livepass shall not be liable for any:
    </p>
    <ul>
      <li>cancellation or postponement</li>
      <li>change in venue, schedule, or lineup</li>
      <li>interruption or disruption</li>
    </ul>
    <p>as these are controlled solely by the Organizer.</p>

    <h4>10. User Conduct and Restrictions</h4>
    <p>You agree not to:</p>
    <ul>
      <li>violate any applicable laws</li>
      <li>misuse the Platform or Services</li>
      <li>attempt to gain unauthorized access to systems</li>
      <li>upload unlawful, harmful, or misleading content</li>
      <li>resell or misuse tickets</li>
    </ul>
    <p>The Company reserves the right to suspend or terminate accounts for violations.</p>

    <h4>11. Content and Intellectual Property</h4>
    <p>All intellectual property rights in the Platform and its Content are owned by or licensed to the Company.</p>
    <p>You may not:</p>
    <ul>
      <li>reproduce, distribute, or exploit any Content</li>
      <li>use trademarks or branding without permission</li>
    </ul>
    <p>
      Any content submitted by you grants the Company a non-exclusive, royalty-free license to use such content for
      platform-related purposes.
    </p>

    <h4>12. Third-Party Services</h4>
    <p>The Platform may include services or content provided by third parties.</p>
    <p>Livepass does not:</p>
    <ul>
      <li>control or endorse such third-party services</li>
      <li>assume responsibility for their performance</li>
    </ul>
    <p>Your interactions with third parties are at your own risk.</p>

    <h4>13. Disclaimer of Warranties</h4>
    <p>The Platform and Services are provided on an "as is" and "as available" basis.</p>
    <p>Livepass makes no representations or warranties regarding:</p>
    <ul>
      <li>accuracy of content</li>
      <li>availability of services</li>
      <li>quality or safety of events</li>
    </ul>

    <h4>14. Limitation of Liability</h4>
    <p>To the maximum extent permitted by law:</p>
    <ul>
      <li>Livepass shall not be liable for indirect, incidental, or consequential damages</li>
      <li>total liability shall not exceed ₹5,000 or the transaction value, whichever is lower</li>
    </ul>

    <h4>15. Indemnity</h4>
    <p>
      You agree to indemnify and hold harmless Livepass and MultiTV Solution against any claims arising from:
    </p>
    <ul>
      <li>your use of the Platform</li>
      <li>violation of these Terms</li>
      <li>infringement of third-party rights</li>
    </ul>

    <h4>16. Termination</h4>
    <p>
      The Company reserves the right to suspend or terminate your access to the Platform at its sole discretion,
      without prior notice, for any violation of these Terms.
    </p>

    <h4>17. Modification of Terms</h4>
    <p>
      The Company may update these Terms from time to time. Continued use of the Platform shall constitute acceptance
      of the revised Terms.
    </p>

    <h4>18. Governing Law and Jurisdiction</h4>
    <p>These Terms shall be governed by the laws of India.</p>
    <p>Courts in New Delhi shall have exclusive jurisdiction.</p>

    <h4>19. Contact Information</h4>
    <p><strong>Company:</strong> MultiTV Solution</p>
    <p><strong>Platform:</strong> Livepass</p>
    <p><strong>Email:</strong> yash.kumar@multitvsolution.com</p>
  </div>
</div>

